import { API } from "aws-amplify";
import { refreshUser } from "../auth/refreshUser";
import { handleAuthError, isAuthError } from "../auth/authErrorHandler";

// Wrapper function for API calls
export const callApi = async (method: string, pathName: string, path: string, options?: any) => {
    let retries = 2; // Allow 2 retries for token refresh
    
    while (retries >= 0) {
        try {
            // Make the API call using Amplify's API module
            if (method === "get") {
                return await API.get(pathName, path, options);
            } else if (method === "post") {
                return await API.post(pathName, path, options);
            } else if (method === "put") {
                return await API.put(pathName, path, options);
            } else if (method === "del") {
                return await API.del(pathName, path, options);
            } else {
                throw new Error(`Invalid API method: ${method}`);
            }
        } catch (error: any) {
            console.error("API call failed:", error);
            
            // Check if it's an authentication error
            if (isAuthError(error)) {
                if (retries > 0) {
                    retries--;
                    console.log(`Authentication error, attempting to handle. Retries left: ${retries}`);
                    
                    // Try to handle the auth error
                    const wasHandled = await handleAuthError(error);
                    
                    if (wasHandled) {
                        // Error was handled (user logged out), throw the error
                        throw error;
                    } else {
                        // Error wasn't handled, try to refresh token
                        try {
                            const accessToken = await refreshUser();
                            
                            if (accessToken) {
                                // Update options with new token
                                const optionsCopy = { ...options };
                                if (!optionsCopy.headers) {
                                    optionsCopy.headers = {};
                                }
                                optionsCopy.headers.Authorization = accessToken;
                                
                                // Retry the API call with new token
                                continue; // This will retry the API call
                            } else {
                                // Token refresh failed, throw the original error
                                throw error;
                            }
                        } catch (refreshError) {
                            console.error("Token refresh failed:", refreshError);
                            throw error; // Throw the original error
                        }
                    }
                } else {
                    // No retries left, throw the error
                    throw error;
                }
            } else {
                // Not an auth error, throw immediately
                throw error;
            }
        }
    }
    
    // This should never be reached, but just in case
    throw new Error("API call failed after all retries");
};
